import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Stack,
  Typography,
  IconButton,
  Divider,
  Avatar,
  TextField,
} from '@mui/material';
import {
  Stethoscope,
  User,
  PencilSimple,
  Shuffle,
  ArrowSquareOut,
} from '@phosphor-icons/react';
import { useState } from 'react';
import ReassignCoachModal from './modals/ReassignCoachModal';
import { Api } from '../api';
import useApiCallable from '../api/use-api-callable';
import { toastErrorHandler } from '../util';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useTypedSelector } from '../reducers';
import { Link } from 'react-router';
import { Model } from '../models';
import SubscriptionDisplay from './SubscriptionDisplay';
import ConfirmModal from './ConfirmModal';

type Props = {
  stats: Api.UserStats.V;
  invalidate: () => void;
};

export default function UserAccountDetails({ stats, invalidate }: Props) {
  const callApi = useApiCallable();
  const viewMode = useTypedSelector((state) => state.auth.viewMode);

  const [reassignModalOpen, setReassignModalOpen] = useState(false);
  const [reassignTarget, setReassignTarget] = useState<
    'coach' | 'physician' | null
  >(null);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [emailValue, setEmailValue] = useState(stats.user.email || '');
  const [deletionRequested, setDeletionRequested] = useState(
    !!stats.user.deletionRequestedAt,
  );
  const [deletionRequestDate, setDeletionRequestDate] = useState<string | null>(
    stats.user.deletionRequestedAt
      ? format(new Date(stats.user.deletionRequestedAt), 'd MMM yyyy')
      : null,
  );
  const [suspendConfirmOpen, setSuspendConfirmOpen] = useState(false);

  const isSuspended = !!stats.user.suspendedAt;

  const founder = stats.clinicians.find((c) => c.type === 'founder');
  const coach = stats.clinicians.find((c) => c.type === 'coach');
  const physician = stats.clinicians.find((c) => c.type === 'physician');

  const getAge = (dob: string | null) => {
    if (!dob) return '-';
    const d = new Date(dob);
    if (isNaN(d.getTime())) return '-';
    const diff = Date.now() - d.getTime();
    const ageDt = new Date(diff);
    return Math.abs(ageDt.getUTCFullYear() - 1970).toString();
  };

  const age = getAge(stats.user.dob);

  const { team, organisation } = Model.User.getTeamAndOrganisation(
    stats.subscription,
  );

  const handleRequestDeletion = async () => {
    const resp = await callApi(
      `/users/${stats.user.id}/request-deletion`,
      { method: 'POST', jsonBody: {} },
      { errorHandler: toastErrorHandler },
    );
    if (resp) {
      setDeletionRequested(true);
      setDeletionRequestDate(format(new Date(), 'd MMM yyyy'));
      invalidate();
      toast.success('Deletion requested');
    }
  };

  const handleCancelDeletion = async () => {
    const resp = await callApi(
      `/users/${stats.user.id}/cancel-deletion-request`,
      { method: 'POST', jsonBody: {} },
      { errorHandler: toastErrorHandler },
    );
    if (resp) {
      setDeletionRequested(false);
      setDeletionRequestDate(null);
      invalidate();
      toast.success('Deletion request cancelled');
    }
  };

  const meClinician = stats.clinicians.find((c) => c.isMe);

  return (
    <>
      <Stack direction="row" spacing={3}>
        <Card sx={{ flex: 1 }}>
          {viewMode === 'admin' && isSuspended && (
            <Alert
              severity="error"
              action={
                <Button
                  variant="outlined"
                  size="small"
                  onClick={async () => {
                    const resp = await callApi(
                      `/users/${stats.user.id}/unsuspend`,
                      {
                        method: 'POST',
                        jsonBody: {},
                      },
                    );
                    if (resp) {
                      invalidate();
                      toast.success('User reactivated');
                    }
                  }}
                  sx={{
                    backgroundColor: 'background.paper',
                    '&:hover': { backgroundColor: 'background.paper' },
                  }}
                >
                  Reactivate user
                </Button>
              }
              sx={{ '& .MuiAlert-action': { alignItems: 'center' } }}
            >
              <AlertTitle>User suspended</AlertTitle>
              This user&apos;s access to the app has been removed.
            </Alert>
          )}
          <CardHeader
            avatar={
              <Avatar>
                <User size={24} />
              </Avatar>
            }
            title="Details"
          />
          <CardContent>
            <Stack spacing={2}>
              <Stack direction="row">
                <Typography variant="subtitle1" sx={{ width: '50%' }}>
                  Name
                </Typography>
                <Typography>{stats.user.name}</Typography>
              </Stack>

              <Stack direction="row">
                <Typography variant="subtitle1" sx={{ width: '50%' }}>
                  Email
                </Typography>
                {isEditingEmail ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                      size="small"
                      type="email"
                      value={emailValue}
                      onChange={(e) => setEmailValue(e.target.value)}
                      placeholder="Enter email"
                    />
                    <Button
                      onClick={async () => {
                        const res = await callApi(
                          `/users/${stats.user.id}/as-admin`,
                          {
                            method: 'PATCH',
                            jsonBody: { email: emailValue },
                          },
                          { errorHandler: toastErrorHandler },
                        );
                        if (res) {
                          toast.success('Email updated');
                          setIsEditingEmail(false);
                          invalidate();
                        }
                      }}
                    >
                      Save
                    </Button>
                  </Stack>
                ) : (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography>{emailValue}</Typography>
                    <IconButton
                      size="small"
                      onClick={() => setIsEditingEmail(true)}
                    >
                      <PencilSimple fontSize="small" />
                    </IconButton>
                  </Stack>
                )}
              </Stack>

              <Stack direction="row">
                <Typography variant="subtitle1" sx={{ width: '50%' }}>
                  Age
                </Typography>
                <Typography>{age}</Typography>
              </Stack>

              <Stack direction="row">
                <Typography variant="subtitle1" sx={{ width: '50%' }}>
                  Country of Residence
                </Typography>
                <Typography>{stats.user.country ?? '-'}</Typography>
              </Stack>

              <Stack direction="row">
                <Typography variant="subtitle1" sx={{ width: '50%' }}>
                  Registration Date
                </Typography>
                <Typography>
                  {format(stats.user.registeredAt, 'd MMM yyyy')}
                </Typography>
              </Stack>

              <Stack direction="row">
                <Typography variant="subtitle1" sx={{ width: '50%' }}>
                  Subscription
                </Typography>
                <Stack spacing={1}>
                  <SubscriptionDisplay
                    {...Model.Subscription.toDisplayProps(stats.subscription)}
                  />
                  {organisation && (
                    <Box>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography>B2B: {organisation.name}</Typography>
                        {'id' in organisation && (
                          <IconButton
                            component={Link}
                            to={`/b2b/${organisation.id}`}
                            size="small"
                            color="primary"
                          >
                            <ArrowSquareOut size={16} />
                          </IconButton>
                        )}
                      </Stack>
                      {team && (
                        <Typography variant="body2" color="textSecondary">
                          {team.name}
                          {'licenceCount' in team &&
                            `, ${team.licenceCount} members`}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Stack>
              </Stack>

              <Stack direction="row">
                <Typography variant="subtitle1" sx={{ width: '50%' }}>
                  Last active
                </Typography>
                <Typography>
                  {stats.user?.lastActiveAt
                    ? format(stats.user.lastActiveAt, 'd MMM yyyy')
                    : '-'}
                </Typography>
              </Stack>

              {viewMode === 'admin' && (
                <Stack direction="row">
                  <Typography variant="subtitle1" sx={{ width: '50%' }}>
                    Newsletter
                  </Typography>
                  <Chip
                    label={
                      stats.user.consentMarketing
                        ? 'Subscribed'
                        : 'Not Subscribed'
                    }
                    size="small"
                    color={stats.user.consentMarketing ? 'success' : 'default'}
                  />
                </Stack>
              )}

              {viewMode === 'clinician' && meClinician && (
                <Stack spacing={2}>
                  <Stack>
                    <Typography variant="overline">APPOINTMENTS</Typography>
                    <Typography>{meClinician.appointmentsHeld} held</Typography>
                    <Typography>
                      {meClinician.appointmentsScheduled} scheduled
                    </Typography>
                    <Typography>
                      {meClinician.sessionsInSubscription} in subscription
                    </Typography>
                  </Stack>
                </Stack>
              )}

              {deletionRequested && (
                <Box>
                  <Stack direction="row">
                    <Typography variant="subtitle1" sx={{ width: '50%' }}>
                      Deletion Requested
                    </Typography>
                    <Chip
                      label={deletionRequestDate}
                      size="small"
                      color="error"
                    />
                  </Stack>
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={handleCancelDeletion}
                    sx={{ mt: 1 }}
                  >
                    Cancel Deletion Request
                  </Button>
                </Box>
              )}

              {viewMode === 'admin' && (
                <Stack direction="row" spacing={3}>
                  {!deletionRequested && (
                    <Button
                      variant="contained"
                      color="error"
                      onClick={handleRequestDeletion}
                    >
                      Request Deletion
                    </Button>
                  )}
                  {!isSuspended && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => setSuspendConfirmOpen(true)}
                    >
                      Suspend User
                    </Button>
                  )}
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Card sx={{ flex: 1 }}>
          <CardHeader
            avatar={
              <Avatar>
                <Stethoscope size={24} />
              </Avatar>
            }
            title="Clinicians"
          />
          <CardContent>
            <Stack spacing={3}>
              {founder && (
                <>
                  <Stack spacing={1.5}>
                    <Typography variant="h6">Founder</Typography>
                    <Typography>{founder.clinicianName}</Typography>

                    <Stack>
                      <Typography
                        variant="overline"
                        fontWeight={600}
                        color="textSecondary"
                      >
                        APPOINTMENTS
                      </Typography>
                      <Typography>{founder.appointmentsHeld} held</Typography>
                      <Typography>
                        {founder.appointmentsScheduled} scheduled
                      </Typography>
                      <Typography>
                        {founder.sessionsInSubscription} in subscription
                      </Typography>
                    </Stack>
                  </Stack>

                  <Divider />
                </>
              )}

              <Stack spacing={1.5}>
                <Stack direction="row" alignItems="center" gap={2}>
                  <Typography variant="h6">Health Coach</Typography>
                  {coach && !Model.UserStats.isB2b(stats) && (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Shuffle size={16} />}
                      onClick={() => {
                        setReassignTarget('coach');
                        setReassignModalOpen(true);
                      }}
                    >
                      Reassign
                    </Button>
                  )}
                </Stack>

                <Stack>
                  <Typography
                    variant="overline"
                    color="textSecondary"
                    fontWeight={600}
                  >
                    Assigned
                  </Typography>
                  <Typography>{coach?.clinicianName ?? '-'}</Typography>
                </Stack>

                <Stack>
                  <Typography
                    variant="overline"
                    fontWeight={600}
                    color="textSecondary"
                  >
                    APPOINTMENTS (ACROSS ALL COACHES)
                  </Typography>
                  <Stack>
                    <Typography>{coach?.appointmentsHeld ?? 0} held</Typography>
                    <Typography>
                      {coach?.appointmentsScheduled ?? 0} scheduled
                    </Typography>
                    <Typography>
                      {coach?.sessionsInSubscription ?? 0} in subscription
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>

              <Divider />

              <Stack spacing={1.5}>
                <Stack direction="row" alignItems="center" gap={2}>
                  <Typography variant="h6">
                    Lifestyle Medicine Physician
                  </Typography>
                  {physician && (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Shuffle size={16} />}
                      onClick={() => {
                        setReassignTarget('physician');
                        setReassignModalOpen(true);
                      }}
                    >
                      Reassign
                    </Button>
                  )}
                </Stack>

                <Stack>
                  <Typography
                    variant="overline"
                    fontWeight={600}
                    color="textSecondary"
                  >
                    Assigned
                  </Typography>
                  <Stack gap={0.5}>
                    <Typography>{physician?.clinicianName ?? '-'}</Typography>
                    {physician?.clinicianName && (
                      <Typography variant="caption" color="textSecondary">
                        Assigned physician for membership support; single
                        sessions may be held with other physicians.
                      </Typography>
                    )}
                  </Stack>
                </Stack>

                {physician?.clinicianName && (
                  <Stack>
                    <Typography
                      variant="overline"
                      fontWeight={600}
                      color="textSecondary"
                    >
                      APPOINTMENTS (ACROSS ALL PHYSICIANS)
                    </Typography>
                    <Typography>
                      {physician?.appointmentsHeld ?? 0} held
                    </Typography>
                    <Typography>
                      {physician?.appointmentsScheduled ?? 0} scheduled
                    </Typography>
                    <Typography>
                      {physician?.sessionsInSubscription ?? 0} in subscription
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <ReassignCoachModal
        open={reassignModalOpen}
        onClose={() => {
          setReassignModalOpen(false);
          setReassignTarget(null);
        }}
        userName={stats.user.name ?? ''}
        currentClinicianId={
          reassignTarget === 'coach' ? coach?.['@id'] : physician?.['@id']
        }
        targetType={reassignTarget || 'coach'}
      />

      {suspendConfirmOpen && (
        <ConfirmModal
          title="Suspend User"
          onClose={() => setSuspendConfirmOpen(false)}
          onConfirm={async ({ setLoading }) => {
            setLoading(true);
            const resp = await callApi(`/users/${stats.user.id}/suspend`, {
              method: 'POST',
              jsonBody: {},
            });
            setLoading(false);
            if (resp) {
              setSuspendConfirmOpen(false);
              invalidate();
              toast.success('User suspended');
            }
          }}
        >
          <Typography>
            Are you sure you want to suspend this user? They will lose access to
            the app.
          </Typography>
          <Typography sx={{ mt: 2 }}>
            Please be aware that this doesn&apos;t cancel their subscription,
            any refunds have to be done manually.
          </Typography>
        </ConfirmModal>
      )}
    </>
  );
}
